// Client Settings Hook

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ClientSettings, Client } from '@/lib/security/types';
import { logConfigChanged } from '@/lib/security/auditLogger';

export function useClientSettings(clientId?: string) {
  const queryClient = useQueryClient();

  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['client-settings', clientId],
    queryFn: async () => {
      if (!clientId) return null;

      const { data, error } = await supabase
        .from('client_settings')
        .select('*')
        .eq('client_id', clientId)
        .maybeSingle();

      if (error) throw error;
      return data as ClientSettings | null;
    },
    enabled: !!clientId,
  });

  const updateSettings = useMutation({
    mutationFn: async (updates: Partial<ClientSettings>) => {
      if (!clientId) throw new Error('No client ID');

      const { data: existing } = await supabase
        .from('client_settings')
        .select('id')
        .eq('client_id', clientId)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('client_settings')
          .update(updates)
          .eq('client_id', clientId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('client_settings')
          .insert({ client_id: clientId, ...updates });
        if (error) throw error;
      }

      // Audit log
      await logConfigChanged('client_settings', clientId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-settings', clientId] });
    },
  });

  return {
    settings,
    isLoading,
    error,
    updateSettings: updateSettings.mutate,
    isUpdating: updateSettings.isPending,
  };
}

export function useClients() {
  const queryClient = useQueryClient();

  const { data: clients, isLoading, error } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('client_name');

      if (error) throw error;
      return data as Client[];
    },
  });

  const createClient = useMutation({
    mutationFn: async (client: { client_name: string; client_type: 'HOSPITAL' | 'SURGICAL_PRACTICE' | 'HEALTH_SYSTEM'; primary_state?: string; is_active?: boolean }) => {
      const { data, error } = await supabase
        .from('clients')
        .insert({
          client_name: client.client_name,
          client_type: client.client_type,
          primary_state: client.primary_state,
          is_active: client.is_active ?? true,
        })
        .select()
        .single();

      if (error) throw error;
      
      // Create default settings for the client
      await supabase.from('client_settings').insert({
        client_id: data.id,
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const updateClient = useMutation({
    mutationFn: async ({ id, client_name, client_type, primary_state, is_active }: { id: string; client_name?: string; client_type?: 'HOSPITAL' | 'SURGICAL_PRACTICE' | 'HEALTH_SYSTEM'; primary_state?: string; is_active?: boolean }) => {
      const updates: Record<string, unknown> = {};
      if (client_name !== undefined) updates.client_name = client_name;
      if (client_type !== undefined) updates.client_type = client_type;
      if (primary_state !== undefined) updates.primary_state = primary_state;
      if (is_active !== undefined) updates.is_active = is_active;

      const { error } = await supabase
        .from('clients')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  return {
    clients: clients || [],
    isLoading,
    error,
    createClient: createClient.mutate,
    updateClient: updateClient.mutate,
    isCreating: createClient.isPending,
    isUpdating: updateClient.isPending,
  };
}
